interface LockedThreadBannerProps {
  studioName?: string;
  studioPhone?: string | null;
  studioEmail?: string | null;
  bookingUrl?: string;
  compact?: boolean;
}

export default function LockedThreadBanner({
  studioName = "the studio",
  studioPhone,
  studioEmail,
  bookingUrl = "/book",
  compact = false,
}: LockedThreadBannerProps) {
  if (compact) {
    return (
      <div
        className="flex-shrink-0 bg-zinc-950 border-t border-zinc-800 px-4 py-4 space-y-3"
        data-testid="locked-thread-banner-compact"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-2.5">
          <span className="text-xl">🔒</span>
          <div>
            <div className="text-white/80 text-sm font-semibold leading-tight">Appointment closed</div>
            <div className="text-white/40 text-xs mt-0.5">This thread is read-only</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <a
            href={bookingUrl}
            data-testid="locked-banner-book-link"
            className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-white transition-colors"
            style={{ background: "#7c3aed" }}
          >
            <span>📅</span> Book again
          </a>
          <a
            href="/contact"
            data-testid="locked-banner-contact-link"
            className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-white/70 border border-zinc-700 transition-colors hover:border-zinc-500 hover:text-white/90"
          >
            <span>✉️</span> Contact us
          </a>
        </div>

        {(studioPhone || studioEmail) && (
          <div className="flex flex-wrap gap-2">
            {studioPhone && (
              <a
                href={`tel:${studioPhone.replace(/\D/g, "")}`}
                data-testid="locked-banner-phone-link"
                className="flex items-center gap-1 text-violet-400 text-xs hover:text-violet-300"
              >
                <span>📞</span>
                <span>{studioPhone}</span>
              </a>
            )}
            {studioEmail && (
              <a
                href={`mailto:${studioEmail}`} // email-exposure-ok: staff-only component (used only in AdminChat + BookingMessages) — artist contacts client
                data-testid="locked-banner-email-link"
                className="flex items-center gap-1 text-violet-400 text-xs hover:text-violet-300"
              >
                <span>@</span>
                <span>{studioEmail}</span>
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex-shrink-0 bg-zinc-950 border-t border-zinc-800 px-4 py-5 space-y-4"
      data-testid="locked-thread-banner"
      style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
    >
      <div className="text-center space-y-1">
        <div className="text-3xl">🔒</div>
        <div className="text-white/80 text-sm font-semibold">This appointment is closed</div>
        <div className="text-white/40 text-xs leading-relaxed">
          This thread is read-only. Start a new booking or reach out directly to {studioName}.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <a
          href={bookingUrl}
          data-testid="locked-banner-book-link"
          className="flex flex-col items-center gap-1 rounded-xl px-3 py-3 text-white transition-colors"
          style={{ background: "#7c3aed" }}
        >
          <span className="text-lg">📅</span>
          <span className="text-xs font-semibold">Book New Appointment</span>
        </a>
        <a
          href="/contact"
          data-testid="locked-banner-contact-link"
          className="flex flex-col items-center gap-1 rounded-xl px-3 py-3 text-white/70 border border-zinc-700 transition-colors hover:border-zinc-500 hover:text-white/90"
        >
          <span className="text-lg">✉️</span>
          <span className="text-xs font-semibold">Contact Us</span>
        </a>
      </div>

      {(studioPhone || studioEmail) && (
        <div
          className="rounded-xl px-3 py-3 space-y-2.5"
          style={{ background: "#0d0d1a", border: "1px solid #2d2d4e" }}
          data-testid="locked-banner-contact-info"
        >
          <div className="text-white/40 text-[10px] font-semibold uppercase tracking-wider">
            Reach {studioName} directly
          </div>
          {studioPhone && (
            <a
              href={`tel:${studioPhone.replace(/\D/g, "")}`}
              data-testid="locked-banner-phone-link"
              className="flex items-center gap-2.5 group"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "#1a1a2e", border: "1px solid #3d3d6e" }}
              >
                <span className="text-sm">📞</span>
              </div>
              <div>
                <div className="text-violet-300 text-sm font-semibold group-hover:text-violet-200 transition-colors">
                  {studioPhone}
                </div>
                <div className="text-white/55 text-[10px]">Tap to call</div>
              </div>
            </a>
          )}
          {studioEmail && (
            <a
              href={`mailto:${studioEmail}`} // email-exposure-ok: staff-only component (used only in AdminChat + BookingMessages) — artist contacts client
              data-testid="locked-banner-email-link"
              className="flex items-center gap-2.5 group"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "#1a1a2e", border: "1px solid #3d3d6e" }}
              >
                <span className="text-sm">✉️</span>
              </div>
              <div>
                <div className="text-violet-300 text-sm font-semibold group-hover:text-violet-200 transition-colors break-all">
                  {studioEmail}
                </div>
                <div className="text-white/55 text-[10px]">Tap to email</div>
              </div>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
